import React, { useState, useEffect } from 'react';
import './Campaigns.scss';
import FlowCanvasJourneyBuilder from './FlowCanvasJourneyBuilder';
import EmailCampaign from './EmailCampaign';
import SMSCampaign from './SMSCampaign';
import WhatsAppCampaign from './WhatsAppCampaign';
import SegmentBuilder from './SegmentBuilder';

const Campaigns = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showJourneyBuilder, setShowJourneyBuilder] = useState(false);
  const [showEmailCampaign, setShowEmailCampaign] = useState(false);
  const [showSMSCampaign, setShowSMSCampaign] = useState(false);
  const [showWhatsAppCampaign, setShowWhatsAppCampaign] = useState(false);
  const [showSegmentBuilder, setShowSegmentBuilder] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      name: "New Candidate Welcome Series",
      type: "email",
      status: "active",
      recipients: 1234,
      opened: 892,
      clicked: 156,
      converted: 34,
      created: "2024-01-15",
      automation: true
    },
    {
      id: 2,
      name: "Interview Reminder SMS",
      type: "sms",
      status: "active",
      recipients: 89,
      opened: 89,
      clicked: 12,
      converted: 8,
      created: "2024-01-20",
      automation: true
    },
    {
      id: 3,
      name: "Application Follow-up",
      type: "message",
      status: "draft",
      recipients: 0,
      opened: 0,
      clicked: 0,
      converted: 0,
      created: "2024-01-25",
      automation: false
    },
    {
      id: 4,
      name: "Interview Confirmation WhatsApp",
      type: "whatsapp",
      status: "active",
      recipients: 156,
      opened: 142,
      clicked: 45,
      converted: 18,
      created: "2024-01-28",
      automation: true
    }
  ]);

  const [stats, setStats] = useState({
    totalCampaigns: 12,
    activeCampaigns: 8,
    totalRecipients: 5420,
    averageOpenRate: 68.5,
    averageClickRate: 12.3,
    conversionRate: 4.8
  });

  const handleCreateCampaign = (type = 'email') => {
    setEditingItem(null);
    switch (type) {
      case 'email':
        setShowEmailCampaign(true);
        break;
      case 'sms':
        setShowSMSCampaign(true);
        break;
      case 'whatsapp':
        setShowWhatsAppCampaign(true);
        break;
      case 'journey':
        // Navigate to journey builder screen
        window.location.href = '/campaigns/journey-builder';
        break;
      case 'segment':
        setShowSegmentBuilder(true);
        break;
      default:
        setShowEmailCampaign(true);
    }
  };

  const handleSaveCampaign = (campaignData) => {
    if (editingItem) {
      setCampaigns(prev => prev.map(c => 
        c.id === editingItem.id ? { ...campaignData, id: editingItem.id } : c
      ));
    } else {
      setCampaigns(prev => [...prev, { ...campaignData, id: Date.now() }]);
    }
    
    // Close all modals
    setShowEmailCampaign(false);
    setShowSMSCampaign(false);
    setShowWhatsAppCampaign(false);
    setShowJourneyBuilder(false);
    setShowSegmentBuilder(false);
    setEditingItem(null);
  };

  const handleCloseModal = () => {
    setShowEmailCampaign(false);
    setShowSMSCampaign(false);
    setShowWhatsAppCampaign(false);
    setShowJourneyBuilder(false);
    setShowSegmentBuilder(false);
    setEditingItem(null);
  };

  const handleCampaignAction = (campaignId, action) => {
    setCampaigns(prevCampaigns =>
      prevCampaigns.map(campaign =>
        campaign.id === campaignId
          ? { ...campaign, status: action === 'activate' ? 'active' : 'paused' }
          : campaign
      )
    );
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-badge active',
      paused: 'status-badge paused',
      draft: 'status-badge draft',
      completed: 'status-badge completed'
    };
    return statusClasses[status] || 'status-badge';
  };

  const getTypeIcon = (type) => {
    const icons = {
      email: '📧',
      sms: '📱',
      whatsapp: '💚',
      message: '💬'
    };
    return icons[type] || '📝';
  };

  return (
    <div className="campaigns-dashboard">
      <div className="container">
        {/* Header */}
        <div className="campaigns-header">
          <div className="header-content">
            <h1>🚀 Campaign Management</h1>
            <p>Create, manage, and optimize your candidate engagement campaigns</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={handleCreateCampaign}
            >
              <span>+ Create Campaign</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="campaign-nav">
          <div className="nav-tabs">
            <button
              className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button
              className={`nav-tab ${activeTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => setActiveTab('campaigns')}
            >
              📋 All Campaigns
            </button>
            <button
              className={`nav-tab ${activeTab === 'automation' ? 'active' : ''}`}
              onClick={() => setActiveTab('automation')}
            >
              🤖 Automation
            </button>
            <button
              className={`nav-tab ${activeTab === 'segmentation' ? 'active' : ''}`}
              onClick={() => setActiveTab('segmentation')}
            >
              🎯 Segments
            </button>
            <button
              className={`nav-tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              📈 Analytics
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            {/* Stats Overview */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.totalCampaigns}</div>
                  <div className="stat-label">Total Campaigns</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon active">🟢</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.activeCampaigns}</div>
                  <div className="stat-label">Active Campaigns</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <div className="stat-number">{formatNumber(stats.totalRecipients)}</div>
                  <div className="stat-label">Total Recipients</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📖</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.averageOpenRate}%</div>
                  <div className="stat-label">Avg Open Rate</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👆</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.averageClickRate}%</div>
                  <div className="stat-label">Avg Click Rate</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.conversionRate}%</div>
                  <div className="stat-label">Conversion Rate</div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="actions-grid">
                <div className="action-card" onClick={() => handleCreateCampaign('email')}>
                  <div className="action-icon">📧</div>
                  <div className="action-info">
                    <div className="action-title">Email Campaign</div>
                    <div className="action-desc">Create targeted email sequences</div>
                  </div>
                </div>
                <div className="action-card" onClick={() => handleCreateCampaign('sms')}>
                  <div className="action-icon">📱</div>
                  <div className="action-info">
                    <div className="action-title">SMS Campaign</div>
                    <div className="action-desc">Send instant SMS notifications</div>
                  </div>
                </div>
                <div className="action-card" onClick={() => handleCreateCampaign('whatsapp')}>
                  <div className="action-icon">💚</div>
                  <div className="action-info">
                    <div className="action-title">WhatsApp Campaign</div>
                    <div className="action-desc">Send rich WhatsApp messages</div>
                  </div>
                </div>
                <div className="action-card" onClick={() => handleCreateCampaign('journey')}>
                  <div className="action-icon">🤖</div>
                  <div className="action-info">
                    <div className="action-title">Journey Builder</div>
                    <div className="action-desc">Create automated workflows</div>
                  </div>
                </div>
                <div className="action-card" onClick={() => handleCreateCampaign('segment')}>
                  <div className="action-icon">🎯</div>
                  <div className="action-info">
                    <div className="action-title">Create Segment</div>
                    <div className="action-desc">Define target audiences</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Campaigns */}
            <div className="recent-campaigns">
              <h3>Recent Campaigns</h3>
              <div className="campaigns-list">
                {campaigns.slice(0, 3).map(campaign => (
                  <div key={campaign.id} className="campaign-item">
                    <div className="campaign-info">
                      <div className="campaign-header">
                        <span className="campaign-type">{getTypeIcon(campaign.type)}</span>
                        <span className="campaign-name">{campaign.name}</span>
                        <span className={getStatusBadge(campaign.status)}>
                          {campaign.status}
                        </span>
                      </div>
                      <div className="campaign-stats">
                        <span>Recipients: {formatNumber(campaign.recipients)}</span>
                        <span>Opens: {formatNumber(campaign.opened)}</span>
                        <span>Clicks: {formatNumber(campaign.clicked)}</span>
                      </div>
                    </div>
                    <div className="campaign-actions">
                      <button className="btn-action">View</button>
                      <button className="btn-action">Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="tab-content">
            <div className="campaigns-section">
              <div className="section-header">
                <h3>All Campaigns</h3>
                <div className="filters">
                  <select className="filter-select">
                    <option value="all">All Types</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="message">Message</option>
                  </select>
                  <select className="filter-select">
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
              
              <div className="campaigns-table">
                <div className="table-header">
                  <div>Campaign</div>
                  <div>Type</div>
                  <div>Status</div>
                  <div>Recipients</div>
                  <div>Performance</div>
                  <div>Actions</div>
                </div>
                
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="table-row">
                    <div className="campaign-cell">
                      <div className="campaign-name">{campaign.name}</div>
                      <div className="campaign-date">Created: {campaign.created}</div>
                    </div>
                    <div className="type-cell">
                      <span className="type-badge">
                        {getTypeIcon(campaign.type)} {campaign.type.toUpperCase()}
                      </span>
                    </div>
                    <div className="status-cell">
                      <span className={getStatusBadge(campaign.status)}>
                        {campaign.status}
                      </span>
                    </div>
                    <div className="recipients-cell">
                      {formatNumber(campaign.recipients)}
                    </div>
                    <div className="performance-cell">
                      <div className="performance-stats">
                        <span>Opens: {campaign.recipients > 0 ? ((campaign.opened / campaign.recipients) * 100).toFixed(1) : 0}%</span>
                        <span>Clicks: {campaign.opened > 0 ? ((campaign.clicked / campaign.opened) * 100).toFixed(1) : 0}%</span>
                      </div>
                    </div>
                    <div className="actions-cell">
                      <div className="action-buttons">
                        <button className="btn-action">📊</button>
                        <button className="btn-action">✏️</button>
                        <button 
                          className="btn-action"
                          onClick={() => handleCampaignAction(
                            campaign.id, 
                            campaign.status === 'active' ? 'pause' : 'activate'
                          )}
                        >
                          {campaign.status === 'active' ? '⏸️' : '▶️'}
                        </button>
                        <button className="btn-action">🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'automation' && (
          <div className="tab-content">
            <div className="automation-section">
              <div className="section-header">
                <h3>🤖 Journey Builder & Automation</h3>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleCreateCampaign('journey')}
                >
                  + Create Journey
                </button>
              </div>
              
              <div className="automation-intro">
                <p>Create automated workflows to engage candidates at every stage of their journey.</p>
              </div>

              <div className="journey-templates">
                <h4>Templates</h4>
                <div className="templates-grid">
                  <div className="template-card">
                    <div className="template-icon">👋</div>
                    <div className="template-info">
                      <h5>Welcome Series</h5>
                      <p>5-step onboarding sequence for new candidates</p>
                      <div className="template-stats">
                        <span>📧 Email</span>
                        <span>5 steps</span>
                        <span>⚡ Auto</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="template-card">
                    <div className="template-icon">📅</div>
                    <div className="template-info">
                      <h5>Interview Process</h5>
                      <p>Automated reminders and follow-ups</p>
                      <div className="template-stats">
                        <span>📱 SMS</span>
                        <span>3 steps</span>
                        <span>⚡ Auto</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="template-card">
                    <div className="template-icon">🎯</div>
                    <div className="template-info">
                      <h5>Re-engagement</h5>
                      <p>Win back inactive candidates</p>
                      <div className="template-stats">
                        <span>📧 Email</span>
                        <span>4 steps</span>
                        <span>⚡ Auto</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="template-card">
                    <div className="template-icon">🏆</div>
                    <div className="template-info">
                      <h5>Offer Process</h5>
                      <p>Smooth offer delivery and acceptance</p>
                      <div className="template-stats">
                        <span>💬 Multi</span>
                        <span>6 steps</span>
                        <span>⚡ Auto</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'segmentation' && (
          <div className="tab-content">
            <div className="segmentation-section">
              <div className="section-header">
                <h3>🎯 Candidate Segmentation</h3>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleCreateCampaign('segment')}
                >
                  + Create Segment
                </button>
              </div>
              
              <div className="segments-grid">
                <div className="segment-card">
                  <div className="segment-header">
                    <h4>New Applicants</h4>
                    <span className="segment-count">1,234 candidates</span>
                  </div>
                  <div className="segment-criteria">
                    <span className="criteria-item">Applied in last 7 days</span>
                    <span className="criteria-item">Status: New</span>
                  </div>
                  <div className="segment-actions">
                    <button className="btn-action">Edit</button>
                    <button className="btn-action">Export</button>
                  </div>
                </div>
                
                <div className="segment-card">
                  <div className="segment-header">
                    <h4>Interview Ready</h4>
                    <span className="segment-count">89 candidates</span>
                  </div>
                  <div className="segment-criteria">
                    <span className="criteria-item">Screening completed</span>
                    <span className="criteria-item">Score > 80%</span>
                  </div>
                  <div className="segment-actions">
                    <button className="btn-action">Edit</button>
                    <button className="btn-action">Export</button>
                  </div>
                </div>
                
                <div className="segment-card">
                  <div className="segment-header">
                    <h4>Inactive Prospects</h4>
                    <span className="segment-count">456 candidates</span>
                  </div>
                  <div className="segment-criteria">
                    <span className="criteria-item">No activity 30+ days</span>
                    <span className="criteria-item">Previously engaged</span>
                  </div>
                  <div className="segment-actions">
                    <button className="btn-action">Edit</button>
                    <button className="btn-action">Export</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="tab-content">
            <div className="analytics-section">
              <div className="section-header">
                <h3>📈 Campaign Analytics</h3>
                <div className="date-filter">
                  <select className="filter-select">
                    <option>Last 30 days</option>
                    <option>Last 90 days</option>
                    <option>This year</option>
                  </select>
                </div>
              </div>
              
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h4>Email Performance</h4>
                  <div className="metric-row">
                    <span>Delivery Rate</span>
                    <span className="metric-value">98.5%</span>
                  </div>
                  <div className="metric-row">
                    <span>Open Rate</span>
                    <span className="metric-value">68.2%</span>
                  </div>
                  <div className="metric-row">
                    <span>Click Rate</span>
                    <span className="metric-value">12.4%</span>
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>SMS Performance</h4>
                  <div className="metric-row">
                    <span>Delivery Rate</span>
                    <span className="metric-value">99.8%</span>
                  </div>
                  <div className="metric-row">
                    <span>Response Rate</span>
                    <span className="metric-value">23.1%</span>
                  </div>
                  <div className="metric-row">
                    <span>Opt-out Rate</span>
                    <span className="metric-value">1.2%</span>
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>Conversion Metrics</h4>
                  <div className="metric-row">
                    <span>Application Rate</span>
                    <span className="metric-value">8.9%</span>
                  </div>
                  <div className="metric-row">
                    <span>Interview Rate</span>
                    <span className="metric-value">15.3%</span>
                  </div>
                  <div className="metric-row">
                    <span>Hire Rate</span>
                    <span className="metric-value">4.7%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Components */}

      {showEmailCampaign && (
        <EmailCampaign
          onClose={handleCloseModal}
          onSave={handleSaveCampaign}
          editingCampaign={editingItem}
        />
      )}

      {showSMSCampaign && (
        <SMSCampaign
          onClose={handleCloseModal}
          onSave={handleSaveCampaign}
          editingCampaign={editingItem}
        />
      )}

      {showWhatsAppCampaign && (
        <WhatsAppCampaign
          onClose={handleCloseModal}
          onSave={handleSaveCampaign}
          editingCampaign={editingItem}
        />
      )}

      {showSegmentBuilder && (
        <SegmentBuilder
          onClose={handleCloseModal}
          onSave={handleSaveCampaign}
          editingSegment={editingItem}
        />
      )}
    </div>
  );
};

export default Campaigns;